import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/utils/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-pre-wrap",
    {
        variants: {
            variant: {
                default: cn("shadow-lg font-bold text-[var(--on-surface-primary-fixed)] bg-[var(--surface-primary-fixed-default)]",
                    "hover:bg-[var(--surface-primary-fixed-hover)]",
                    "disabled:bg-[var(--surface-disabled-variant)] disabled:text-[var(--on-surface-variant-2)] disabled:bg-[var(--surface-disabled-variant)]",
                    "pressed:bg-[var(--surface-primary-fixed-pressed)]"
                ),
                secondary:
                    cn("shadow-lg font-bold text-[var(--on-surface-primary-default-variant)] bg-[var(--surface-default)] ",
                        "border-[1px] border-[var(--outline-primary-default)]",
                        "hover:border-[var(--outline-primary-hover)] hover:bg-[var(--surface-primary-hover-variant-2)] hover:text-[var(--on-surface-primary-hover-variant)]",
                        "disabled:bg-[var(--surface-disabled)] disabled:border-[var(--outline-disabled)] disabled:text-[var(--on-surface-variant-2)]",
                        "pressed:bg-[var(--surface-primary-pressed-variant)] pressed:border-[var(--outline-primary-pressed)] pressed:text-[var(--on-surface-primary-pressed-variant)]"
                    ),
                tertiary: "",
                ghost:
                    "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
                link: "underline text-[var(--on-surface-primary-default-variant)] hover:text-[var(--on-surface-primary-hover-variant)] pressed:text-[var(--on-surface-primary-pressed-variant)] disabled:text-[var(--on-surface-variant-2)]"
            },
            size: {
                default: "min-w-96 min-h-24 rounded-lg py-4 px-8 has-[>svg]:px-3",
                sm: "min-h-24 rounded-md  py-4 gap-1.5 px-3 has-[>svg]:px-2.5",
                lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
                icon: "h-12 w-12 p-4",
                "icon-sm": "size-8",
                "icon-lg": "size-10",
                link: "w-full"
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

function Button({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean
    }) {
    const Comp = asChild ? Slot : "button"

    return (
        <Comp
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

export { Button, buttonVariants }
